/**
 * @plerity/learn-mode — public types.
 *
 * The overlay is behaviour only. Everything app-specific is injected:
 *   - WHERE tips come from  → `resolveStaticTip` + optional `loadRemoteTips`
 *   - EXTRA anchors         → optional `collectExtraAffordances` (portal tours)
 *   - HOW a rich body draws → optional `renderBody` (portal Tiptap)
 *   - WHAT it looks like    → required `classNames` (every Tailwind class string
 *     lives in the consuming app, so the app's own Tailwind scan generates it —
 *     no `@source` entry for this package is ever needed)
 */

import type { ReactNode } from "react";
import type { ObstacleSelectorOptions } from "./collectObstacleRects";

/** Value of `LearnModeZIndex.layer = "tooltip"` (tokens-apps preset `zIndex.tooltip`). */
export const TOOLTIP_Z_INDEX = 1050;

/** One help tip, normalised across every tip source. */
export interface LearnTip {
  /** Short, plain-language title (popover heading + default badge label). */
  title: string;
  /** Plain-text body. Ignored when `renderBody` returns a node for this tip. */
  body?: string | null;
  /** Optional bullet points rendered under the body. */
  bullets?: readonly string[] | null;
  /** Opaque rich-body payload (e.g. parsed Tiptap blocks) for `renderBody`. */
  rich?: unknown;
  /** Show a "Start tour" action (dispatches `plerity:start-tour`). */
  tourAction?: boolean;
  /** Show a "Watch" action (dispatches `plerity:open-help`). */
  videoAction?: boolean;
}

/** A tip bound to a concrete DOM anchor. */
export interface LearnAffordance {
  /** Stable identity — `help:<contextKey>` (`#n` suffix for repeats) or a caller key. */
  key: string;
  /** The `data-help-context` value, or null for caller-supplied (tour) anchors. */
  contextKey: string | null;
  /** The element the badge is anchored to. */
  target: HTMLElement;
  tip: LearnTip;
}

/** Caller-supplied anchor (portal tour steps). Claimed BEFORE the context scan. */
export interface ExtraAffordance {
  key: string;
  target: HTMLElement;
  tip: LearnTip;
}

/** Loads server-side tips for a page, keyed by context key. Rejections are swallowed. */
export type RemoteTipLoader = (pageKey: string, signal: AbortSignal) => Promise<ReadonlyMap<string, LearnTip>>;

/**
 * `anchored` — the badge is portaled INTO its target (target promoted to
 *   `position: relative`, z-lifted while open). Tracks scroll natively.
 * `floating` — badges live in one body-level `position: fixed` layer, clamped
 *   to the viewport and re-measured on scroll/resize (rAF-throttled). Survives
 *   targets that can't take a child (FullCalendar cells, overflow scrollers).
 */
export type LearnBadgePlacement = "anchored" | "floating";

/** Tailwind skin. Every key is a class string owned by the consuming app. */
export interface LearnModeClassNames {
  /** Badge button — base classes (always applied). */
  badge: string;
  /** Badge button — added when its popover is closed. */
  badgeIdle: string;
  /** Badge button — added when its popover is open. */
  badgeActive: string;
  /** `anchored` only — the absolutely-positioned span wrapping the badge. */
  badgeAnchor?: string;
  /**
   * `floating` + `avoidObstacles` only — added to the badge button when the
   * last-resort reduced-size spot wins (the badge is also given an inline
   * 80%-size width/height). Typically a quieter look, e.g. lower idle opacity.
   */
  badgeReduced?: string;
  /** Popover container (positioning/size are applied inline). */
  popover: string;
  /** Row holding the title and (optionally) the close button. */
  header?: string;
  title: string;
  /** Close button inside the popover (only when `showCloseButton`). */
  closeButton?: string;
  /** Plain-text body paragraph. */
  body: string;
  /** Wrapper around a `renderBody` node. Defaults to `body`. */
  richBody?: string;
  bulletList: string;
  bulletItem?: string;
  /** When set, each bullet gets an aria-hidden "•" marker span with this class. */
  bulletMarker?: string;
  /** Row holding the tour / video action buttons. */
  actions?: string;
  tourButton?: string;
  videoButton?: string;
}

/** Stacking values. Numbers or CSS expressions such as `var(--z-help-popover)`. */
export interface LearnModeZIndex {
  /** `floating` badges (inline z-index). Ignored for `anchored` (class-driven). */
  badge?: number | string;
  /**
   * The open popover. Optional since `layer` was added: omit it (with no
   * `layer`) to leave the popover's z-index to your `classNames.popover`.
   */
  popover?: number | string;
  /** `anchored` only — z-index applied to the target while its popover is open. */
  targetLift?: string;
  /**
   * Named stacking tier that supplies defaults for `badge` and `popover` when
   * they are not given explicitly. `"tooltip"` = `TOOLTIP_Z_INDEX` (1050, the
   * `zIndex.tooltip` value of the `@plerity/tokens-apps` Tailwind preset), safely
   * above app chrome, chat FABs and modal overlays. Explicit values win.
   */
  layer?: "tooltip";
}

export interface LearnModeOverlayProps {
  pageKey: string;
  /** Resolve an authored (version-controlled) tip. Static ALWAYS wins over remote. */
  resolveStaticTip: (contextKey: string) => LearnTip | null;
  /** Optional server tips; only used for context keys with no static tip. */
  loadRemoteTips?: RemoteTipLoader;
  /** Optional anchors claimed before the `[data-help-context]` scan (tours). */
  collectExtraAffordances?: (pageKey: string) => ExtraAffordance[];
  placement: LearnBadgePlacement;
  classNames: LearnModeClassNames;
  zIndex: LearnModeZIndex;
  /** Rich-body renderer. Return null/undefined to fall back to the plain body. */
  renderBody?: (tip: LearnTip) => ReactNode;
  /** Badge accessible name. Default: `Help: <tip title>`. */
  badgeLabel?: (affordance: LearnAffordance) => string;
  /** Render a close (×) button in the popover header. Default true. */
  showCloseButton?: boolean;
  /** Open on badge hover and close when the pointer leaves the popover. Default false. */
  openOnHover?: boolean;
  /**
   * Context keys that never get a badge even though a tip resolves for them
   * (e.g. a Konva canvas wrapper where a corner badge would sit on pointer
   * interaction). Skipped entirely during the scan. Default: none.
   */
  excludeContextKeys?: readonly string[];
  /**
   * Obstacle-aware badge placement. Only meaningful with `placement="floating"`
   * (ignored for `anchored`). When true, each badge is placed on the first of 6
   * candidate spots (then an outward search, then a reduced-size spot) that
   * `document.elementsFromPoint` shows is free of controls / media / text; if
   * none is free NO badge is rendered for that anchor. Default false — the
   * original viewport-clamped top-right placement, unchanged.
   */
  avoidObstacles?: boolean;
  /** Tuning for `avoidObstacles` (selector sets, sample inset). Defaults match social's. */
  obstacleOptions?: ObstacleSelectorOptions;
  /** Tour action handler. Default: dispatch `plerity:start-tour` `{ pageKey }` on window. */
  onStartTour?: (pageKey: string) => void;
  /** Video action handler. Default: dispatch `plerity:open-help` on window. */
  onOpenHelp?: () => void;
}
